import { Capacitor } from '@capacitor/core';

/*
  TELEFON BILDIRIMI KAYDI
  -----------------------
  Bildirimin telefona ulasmasi icin once o telefonun sunucuya
  KAYDEDILMESI gerekiyor: iOS her uygulamaya bir cihaz jetonu veriyor,
  sunucu bildirimi o jetona gonderiyor.

  NEDEN AYRI DOSYA: bu kayit daha once hem personel hem muvekkil
  tarafinda ayri ayri, hatalari tamamen yutarak yapiliyordu
  (catch (e) {}). Kayit basarisiz oldugunda hicbir iz kalmiyordu.
  Sunucu kaydi "kayitli cihaz=0" diyordu: kayit hic olusmamisti.

  SURE SINIRI NEDEN VAR: eklenti cagrilari bazen HIC CEVAP VERMIYOR --
  hata da atmiyorlar, sonsuza kadar bekliyorlar. O zaman hem kayit
  tamamlanmiyor hem de ayar karti "bakiliyor" halinde takilip ekranda
  hic gorunmuyordu. Her cagriya sure siniri konuldu.
*/

export const DURUM = {
  TARAYICI: 'tarayici',        // uygulama degil, tarayicidan bakiliyor
  EKLENTI_YOK: 'eklentiYok',   // uygulamanin eski surumu
  IZIN_YOK: 'izinYok',         // kullanici bildirim iznini vermemis
  JETON_YOK: 'jetonYok',       // izin var ama Apple jeton vermedi
  SUNUCU_HATASI: 'sunucuHatasi',
  HAZIR: 'hazir',
};

export const durumMetni = (durum, ayrinti) => {
  switch (durum) {
    case DURUM.TARAYICI:
      return 'Telefon bildirimleri yalnızca uygulamada çalışır.';
    case DURUM.EKLENTI_YOK:
      return 'Uygulamanın bu sürümü bildirim gönderemiyor. App Store’dan güncelleyin.';
    case DURUM.IZIN_YOK:
      return 'Bildirim izni verilmemiş. Açmak için düğmeye basın.';
    case DURUM.JETON_YOK:
      return 'Telefon bildirim adresi alınamadı. İnternet bağlantınızı kontrol edip tekrar deneyin.';
    case DURUM.SUNUCU_HATASI:
      return `Kayıt sunucuya ulaşmadı${ayrinti ? ` (${ayrinti})` : ''}. Tekrar deneyin.`;
    case DURUM.HAZIR:
      return 'Telefon bildirimleri açık.';
    default:
      return 'Durum kontrol ediliyor…';
  }
};

/** Cevapsiz kalan cagrilar icin sure siniri. */
export const sureSiniri = (soz, saniye, varsayilan) =>
  Promise.race([
    Promise.resolve(soz).catch(() => varsayilan),
    new Promise((coz) => setTimeout(() => coz(varsayilan), saniye * 1000)),
  ]);

const yerli = () =>
  typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform();

/**
 * Bu telefonu bildirim icin kaydeder.
 *
 * @param {(jeton: string, platform: string) => Promise<any>} kaydet
 *        Sunucuya yazan cagri (personel ve muvekkil icin farkli uc).
 * @param {boolean} izinIste  false ise yalnizca mevcut izne bakilir,
 *        kullaniciya sistem penceresi gosterilmez.
 * @returns {Promise<{durum: string, ayrinti?: string}>}
 */
export const bildirimiKaydet = async (kaydet, izinIste = true) => {
  if (!yerli()) return { durum: DURUM.TARAYICI };

  let PushNotifications;
  try {
    ({ PushNotifications } = await import('@capacitor/push-notifications'));
    if (!PushNotifications) return { durum: DURUM.EKLENTI_YOK };
  } catch (e) {
    return { durum: DURUM.EKLENTI_YOK };
  }

  try {
    let izin = await sureSiniri(
      PushNotifications.checkPermissions(), 6, { receive: 'prompt' });

    if (izin?.receive !== 'granted') {
      if (!izinIste) return { durum: DURUM.IZIN_YOK };
      izin = await sureSiniri(
        PushNotifications.requestPermissions(), 40, { receive: 'denied' });
    }
    if (izin?.receive !== 'granted') return { durum: DURUM.IZIN_YOK };

    /*
      Jetonu bekle. Apple jetonu 'registration' olayiyla gonderiyor.

      SIRALAMA KRITIK: addListener Capacitor'da ASENKRON. Dinleyici
      native tarafa kaydolmadan register() cagrilirsa olay kacirilir ve
      jeton hic gelmez -- kart "Telefon bildirim adresi alinamadi"
      diyordu. Bu yuzden once iki dinleyici de AWAIT ediliyor, register
      ancak ondan sonra cagriliyor.
    */
    let bitir;
    const jetonSozu = new Promise((coz) => {
      let bitti = false;
      bitir = (deger) => {
        if (bitti) return;
        bitti = true;
        coz(deger);
      };
      setTimeout(() => bitir(null), 25000);
    });

    try {
      await PushNotifications.addListener('registration', (t) => bitir(t?.value || null));
      await PushNotifications.addListener('registrationError', () => bitir(null));
    } catch (e) {
      return { durum: DURUM.JETON_YOK };
    }

    try {
      await PushNotifications.register();
    } catch (e) {
      bitir(null);
    }

    const jeton = await jetonSozu;
    if (!jeton) return { durum: DURUM.JETON_YOK };

    try {
      await kaydet(jeton, Capacitor.getPlatform());
      return { durum: DURUM.HAZIR };
    } catch (e) {
      const kod = e?.response?.status;
      return { durum: DURUM.SUNUCU_HATASI, ayrinti: kod ? `hata ${kod}` : undefined };
    }
  } catch (e) {
    return { durum: DURUM.JETON_YOK };
  }
};
